import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  type ContinuityTraversalStep,
  traversalStepsForSurface,
} from "../../lib/continuityTraversalNav";
import "./ContinuityTraversalRail.css";

type StepStatus = "completed" | "current" | "future";

function stepStatus(step: ContinuityTraversalStep, current: ContinuityTraversalStep | null): StepStatus {
  if (!current) return "future";
  if (step.index < current.index) return "completed";
  if (step.index === current.index) return "current";
  return "future";
}

function RailLink({ step, status }: { step: ContinuityTraversalStep; status: StepStatus }) {
  const className = `xo-traversal-rail__item xo-traversal-rail__item--${status}`;
  const indexLabel = status === "completed" ? "✓" : String(step.index);
  const inner = (
    <>
      <span className="xo-traversal-rail__index">{indexLabel}</span>
      <span className="xo-traversal-rail__label">{step.shortLabel}</span>
      <span className="xo-traversal-rail__hint" role="tooltip">
        {step.hint}
      </span>
    </>
  );
  if (step.external) {
    return (
      <li className={className}>
        <a className="xo-traversal-rail__link" href={step.href} target="_blank" rel="noopener noreferrer">
          {inner}
        </a>
      </li>
    );
  }
  return (
    <li className={className}>
      <Link className="xo-traversal-rail__link" to={step.href}>
        {inner}
      </Link>
    </li>
  );
}

export type ContinuityTraversalRailProps = {
  currentStep: ContinuityTraversalStep | null;
};

export function ContinuityTraversalRail({ currentStep }: ContinuityTraversalRailProps) {
  const callFinale = currentStep?.id === "call";
  const hostname =
    typeof globalThis !== "undefined" && "location" in globalThis
      ? (globalThis as unknown as { location: { hostname: string } }).location.hostname
      : "";
  const pathname =
    typeof globalThis !== "undefined" && "location" in globalThis
      ? (globalThis as unknown as { location: { pathname: string } }).location.pathname
      : "/";
  const steps = traversalStepsForSurface(hostname, pathname);
  const railClass = callFinale ? "xo-traversal-rail xo-traversal-rail--call-finale" : "xo-traversal-rail";
  const stripClass = callFinale ? "xo-traversal-strip xo-traversal-strip--call-finale" : "xo-traversal-strip";

  return (
    <>
      <nav className={railClass} aria-label="Continuity traversal sequence" data-testid="continuity-traversal-rail">
        <ol className="xo-traversal-rail__list">
          {steps.map((step) => (
            <RailLink key={step.id} step={step} status={stepStatus(step, currentStep)} />
          ))}
        </ol>
      </nav>
      <div className={stripClass} aria-label="Continuity traversal progress" data-testid="continuity-traversal-strip">
        <div className="xo-traversal-strip__track">
          {steps.map((step) => {
            const status = stepStatus(step, currentStep);
            const className = `xo-traversal-strip__step xo-traversal-strip__step--${status}`;
            const label = (
              <>
                <span className="xo-traversal-strip__num">{status === "completed" ? "✓" : step.index}</span>
                <span className="xo-traversal-strip__name">{step.shortLabel}</span>
              </>
            );
            if (step.external) {
              return (
                <a key={step.id} className={className} href={step.href} target="_blank" rel="noopener noreferrer" title={step.hint}>
                  {label}
                </a>
              );
            }
            return (
              <Link key={step.id} className={className} to={step.href} title={step.hint}>
                {label}
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function ContinuityTraversalShell({
  currentStep,
  children,
}: {
  currentStep: ContinuityTraversalStep | null;
  children: ReactNode;
}) {
  return (
    <div className="xo-traversal-shell">
      <ContinuityTraversalRail currentStep={currentStep} />
      <div className="xo-traversal-body">{children}</div>
    </div>
  );
}
